import { env } from "../core/env.js";

// Port of src/user/utilities/use_ga_tracker.js.
//
// React used react-ga, which only ever spoke Universal Analytics (analytics.js) — a property type
// Google shut down, and the library itself is unmaintained (last release 2020). The BEHAVIOUR is
// what is ported: initialise once with the active client as the analytics user id, then send one
// pageview per route change. The transport is the current gtag.js snippet instead of react-ga.
//
// `REACT_APP_GA_KEY` is set for production builds only; everywhere else it is empty and this module
// injects nothing at all — no script tag, no dataLayer, no requests.

export const gtagScriptSrc = (key) =>
  `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(key)}`;

// react-ga was given `location.pathname + location.search`; GA4's page_path keeps the same shape.
export const pagePath = (pathname, search) => `${pathname || "/"}${search || ""}`;

let activeKey = null;

// Google's snippet pushes the `arguments` OBJECT (not an array) — gtag.js reads it back that way,
// so this has to stay a plain function rather than a rest-parameter arrow.
function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

// Loads gtag.js and configures the property. Returns whether analytics was actually enabled, so a
// caller (and the test) can tell "configured" from "no key". Safe to call more than once.
export function initAnalytics(key = env.gaKey, userId = null) {
  if (!key || activeKey) return false;
  activeKey = key;

  const script = document.createElement("script");
  script.async = true;
  script.src = gtagScriptSrc(key);
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  gtag("js", new Date());
  // Pageviews are sent explicitly per route change below; the automatic one would double-count the
  // first screen and would never fire again in a client-side-routed app.
  gtag("config", key, { user_id: userId || undefined, send_page_view: false });
  return true;
}

export function trackPageview(path) {
  if (!activeKey) return false;
  gtag("event", "page_view", { page_path: path, page_location: window.location.href });
  return true;
}

// Test seam: the module keeps one-shot init state, which a test needs to clear between cases.
export function resetAnalyticsForTest() {
  activeKey = null;
}
